<template>
  <div class="flex flex-row gap-y-1" v-show="isShow" :class="fullWidth && '!w-full'">
    <div :class="direction" class="w-full flex gap-x-4 gap-y-1 text-brand-black-secondary">
      <label
        v-if="label"
        :for="fieldName"
        :class="labelClass"
        class="relative flex items-center gap-x-2"
      >
        <div>
          <span> {{ t9nLabel }}</span>
          <span v-if="isRequired" class="text-brand-red ps-1">*</span>
        </div>
        <HintTippy v-if="hint" :hint="hint"></HintTippy>
      </label>
      <div class="w-full flex flex-col justify-start items-start gap-2">
        <template v-if="processedAddressTable.length === 0">
          <InfoBar variant="info">
            <span class="pt-1 flex-1 text-white text-justify text-base">
              {{ $t('noNewAddressFound') }}
            </span>
          </InfoBar>
        </template>
        <DataTableSkeleton v-if="!isAddressLoaded" class="w-full" />
        <DataTable :data-set="processedAddressTable" v-if="processedAddressTable" class="w-full">
          <template #headerCell="{ label }">
            <span v-if="label == 'addressId'"></span>
          </template>
          <template #cell="{ columnName, columnValue, rowIndex }">
            <!-- addressId for select radio -->
            <div v-if="columnName == 'addressId'" class="w-full h-full flex gap-1">
              <input
                type="radio"
                :key="columnValue"
                :value="columnValue"
                :checked="selectedAddressID == columnValue"
                @change="setAddressDataHandle(addressDataTable[rowIndex], columnValue)"
              />
            </div>
            <span v-else-if="needHtmlFormatColumn.includes(columnName)" v-html="columnValue"></span>
            <div class="min-h-4" v-else>{{ columnValue }}</div>
          </template>
        </DataTable>
        <div class="flex flex-col gap-y-2 w-full">
          <template v-for="field in props.templateSchema" :key="field.field_name">
            <component
              v-if="canShow(field.showRules, field.device_type)"
              :is="findComponent(field.component)"
              :field-name="field.field_name"
              :dependent-on="field.dependent_on"
              :on-change="field.on_change"
              :on-blur="field.on_blur"
              :default-value="formStore.draft?.request?.[field.field_name] || field.default_value"
              :is-disabled="field.disabled"
              :server-date-format="field.server_date_format"
              :min-date="field.min_date"
              :max-date="field.max_date"
              :prefixDisabled="field.prefixDisabled"
              :showRules="field.showRules"
              :radioOptions="field.radio_options"
              :sequenceOptions="field.sequence_options"
              :resetValues="field.reset_values"
              :layout="section?.form_style?.web?.layout"
              :resetValue="field.resetValue"
              v-bind="{ ...field, ...layoutClasses(section?.form_style?.web?.layout) }"
            ></component>
          </template>
          <!-- update / save as new / temporary -->
          <div
            class="w-full p-2 flex flex-col justify-center items-start border-2 border-primary border-dashed"
            v-if="enableNewSharedUserAddreses && checkIsModifyFlag && !IsFamilyBookServices"
          >
            <div>{{ $t('youMadeChangesDidYouWantToSave') }}</div>
            <div class="flex justify-center items-center gap-2">
              <input
                type="radio"
                name="localaddressRadio"
                value="1"
                v-model="formStore.extraMap.localAddressUserActionEnum"
              />
              {{ $t('saveAsNewAddress') }}
            </div>
            <div class="flex justify-center items-center gap-2">
              <input
                type="radio"
                name="localaddressRadio"
                value="2"
                v-model="formStore.extraMap.localAddressUserActionEnum"
              />
              {{ $t('updateCurrentAddress') }}
            </div>
            <div class="flex justify-center items-center gap-2">
              <input
                type="radio"
                name="localaddressRadio"
                value="3"
                v-model="formStore.extraMap.localAddressUserActionEnum"
              />
              {{ $t('continueWithoutSaveAddressChanges') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onBeforeMount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFormStore } from '@/stores/form'
import useVisible from '@/composables/useVisible'
import { useRoute } from 'vue-router'
import DataTable from '@/components/DataTable/index.vue'
import HintTippy from '@/components/Form/HintTyppy.vue'
import lodashGet from 'lodash/get'
import lodashSet from 'lodash/set'

import CommonButton from '@/components/Form/CommonButton.vue'
import CheckboxTicker from '@/components/Form/CheckboxTicker.vue'
import TextRenderer from '@/components/Form/TextRenderer.vue'
import ComboText from '@/components/Form/ComboText.vue'
import DropdownSearch from '@/components/Form/DropdownSearch.vue'
import InfoBar from '@/components/Form/InfoBar.vue'
import InfoLabel from '@/components/Form/InfoLabel.vue'
import radio_group from '@/components/Form/RadioGroup.vue'
import RecaptchaTicker from '@/components/Form/RecaptchaTicker.vue'
import SpacerComponent from '@/components/Form/SpacerVue.vue'
import TextInput from '@/components/Form/TextInput.vue'
import TwowayDropdown from '@/components/Form/TwowayDropdown.vue'
import VisaFileNumber from '@/components/Form/VisaFileNumber.vue'
import DatepickerCalendar from '@/components/Form/DatepickerCalendar.vue'
import TimePicker from '@/components/Form/TimePicker.vue'
import DateTimePicker from '@/components/Form/DateTimePicker.vue'
import MobileButtonList from '@/components/Form/MobileButtonList.vue'

import { useAuthStore } from '@/stores/auth'
import client_lookup from '@/assets/json/clientLookups.json'
import apiHelperService from '@/services'
import useAddressFormFormat from '@/composables/actions/useAddressFormFormat.js'
import useAddressFormDataAction from '@/composables/actions/useAddressFormDataAction'

import DataTableSkeleton from '@/components/skeletons/DatatableSkeleton.vue'

const props = defineProps({
  addressType: String,
  fullWidth: Boolean,
  visibleColumns: {
    type: Array,
    required: true
  },
  templateSchema: {
    type: Array,
    required: true
  },
  layout: {
    type: String,
    default: 'one-column'
  },
  hint: String,
  labelValue: String,
  options: {
    type: Array,
    required: true
  },
  fieldName: {
    type: String,
    required: true
  },
  label: {
    type: String,
    required: true
  },
  modelValue: {
    type: [String, Number]
  },
  rules: Array,
  showRules: Object,
  defaultValue: String,
  direction: {
    type: String,
    default: 'flex-row md:items-start '
  },
  labelClass: {
    type: String,
    default: () => 'w-full md:w-1/3'
  },
  dependentOn: String,
  sequenceOptions: Array
})
const route = useRoute()
const formStore = useFormStore()
const i18n = useI18n()
const authStore = useAuthStore()
const addressFormFormat = useAddressFormFormat()

const needHtmlFormatColumn = ref([])
const addressDataTable = ref([])
const processedAddressTable = ref([])
const selectedAddressID = ref()
const enableNewSharedUserAddreses = ref(true)
const checkIsModifyFlag = ref(false)
const isAddressLoaded = ref(false)

const canShow = (showRules, deviceType) => {
  if (deviceType === 'mobile') return false
  if (!showRules) return true

  return findShowRule(
    showRules.any || showRules.all,
    showRules.any ? 'any' : 'all',
    getBindObj(route.params.serviceId)
  )
}

const IsFamilyBookServices = computed(() => {
  return [582, 587].includes(route.params.serviceTransactionId)
})
// Due to the different address type, the address data is different, so the data needs to be formatted
const checkIsModifyAddressObj = computed(() => {
  if (props.addressType === 'LocalAddresses') {
    const diffMap = {}
    props.templateSchema.map((field) => {
      if (field.field_name === 'localAddress.name') {
        lodashSet(diffMap, 'localAddress', lodashGet(formStore.form.values, field.field_name))
      } else {
        lodashSet(diffMap, field.field_name, lodashGet(formStore.form.values, field.field_name))
      }
    })
    return diffMap
  }
})

// check if enableNewSharedUserAddreses is true
const initEnableNewSharedUserAddreses = () => {
  const serviceTransactionId = route.params.serviceTransactionId
  const { resident, citizen, gcc, gcc_citizen, visaHolder, dubaiResident, establishment } =
    client_lookup.application
  const { issuePassport, replacePassport, passportLosing } = client_lookup.ServiceTransactions

  const isInServiceList = [issuePassport, replacePassport, passportLosing].includes(
    serviceTransactionId
  )

  const isInRoleList = [
    resident,
    citizen,
    gcc,
    gcc_citizen,
    visaHolder,
    dubaiResident,
    establishment
  ].includes(authStore.user.applicationId)

  if (!isInRoleList || isInServiceList) {
    enableNewSharedUserAddreses.value = false
  }
}
// Format the data table column that need to be displayed in html format
props?.visibleColumns?.forEach((column) => {
  if (Array.isArray(column.value)) {
    needHtmlFormatColumn.value.push(i18n.t(column.label))
  }
})

// Only show some columns
const formateAddressTable = async (item) => {
  const resMap = {}

  await Promise.all(
    props.visibleColumns.map(async (column) => {
      if (Array.isArray(column.value)) {
        return (resMap[i18n.t(column.label)] = column.value
          .map((nestedColumn) => {
            return item[nestedColumn]
          })
          .join('<br\>'))
      }
      if (IsFamilyBookServices.value) {
        resMap[i18n.t('area')] = item.localAreaId
      }
      if (column.format) {
        resMap[i18n.t(column.label)] = await addressFormFormat[column.format](
          lodashGet(item, column.value),
          column,
          item
        )
      } else {
        return (resMap[i18n.t(column.label)] = lodashGet(item, column.value))
      }
    })
  )
  return resMap
}
// init local address from api
const getLocalAddresses = async () => {
  isAddressLoaded.value = false
  const { childEstablishmentId, sponsoredUN } = route.query
  const params = {}

  if (childEstablishmentId) {
    params.childEstablishmentId = childEstablishmentId
  } else {
    params.personUnifiedNumber = sponsoredUN
  }
  const { path } = props.options.api

  try {
    let res = await apiHelperService.get(path, {
      params
    })
    if (res.length > 0) {
      addressDataTable.value = res
      processedAddressTable.value = await Promise.all(res.map(formateAddressTable))
    }
  } catch (error) {
    console.error('Error in getLocalAddresses', error)
  }
  isAddressLoaded.value = true
}

const waitFn = () => {
  return new Promise((res, rej) => {
    setTimeout(() => {
      res(true)
    }, 1 * 1000)
  })
}

const setDeliveryAddressData = async (address) => {
  if (address) {
    const { Tawzea, EmiratePost, Zajel, Amer } = client_lookup.deliveyCompany
    let dataObj = {
      deliveryMethodId: address.deliveryMethodId,
      deliveryCompanyId: address.deliveryCompanyId,
      currentMobileNumber: address.mobileNumber,
      secondMobileNumber: address.secoundMobileNumber,
      deliveryMobileNumber: address.mobileNumber,
      deliveryAddressDetails: address.addressDetails
    }

    formStore.form.setValues(dataObj)
    // await nextTick()
    await waitFn()
    // !!! Don't change the order of the following code, destructuring the form values will missing the reaction.
    const { deliveryCompanyId } = formStore.form.values

    if (deliveryCompanyId == Tawzea) {
      dataObj = {
        ...dataObj,
        tawzeaDeliveryEmirateId: address.emirateId,
        tawzeaDeliveryCityId: address.cityId,
        tawzeaDeliveryAreaId: address.areaId,
        landMarkId: address.landMarkId,
        postalBranchId: address.postalBranchId
      }
    } else if (deliveryCompanyId == EmiratePost) {
      dataObj = {
        ...dataObj,
        tawzeaDeliveryEmirateId: address.emirateId,
        tawzeaDeliveryCityId: address.cityId,
        tawzeaDeliveryAreaId: address.areaId,
        landMarkId: address.landMarkId,
        postalBranchId: address.postalBranchId,
        emiratePostDeliveryEmirateId: address.emirateId,
        emiratePostDeliveryCityId: address.cityId,
        emiratePostDeliveryAreaId: address.areaId,
        sponsoredPobox: address.poBoxNumber,
        deliveryLocalMobile: address.mobileNumber,
        ...(address.poBoxFlag && { havePoBox: Number(address.poBoxFlag) })
      }
    } else if (deliveryCompanyId == Zajel) {
      dataObj = {
        ...dataObj,
        zajelDeliveryEmirateId: address.emirateId,
        zajelDeliveryCityId: address.cityId,
        zajelDeliveryAreaId: address.areaId
      }
    } else if (deliveryCompanyId == Amer) {
      dataObj = {
        ...dataObj,
        amerDeliveryEmirateId: address.emirateId,
        amerDeliveryCityId: address.cityId,
        amerDeliveryAreaId: address.areaId,
        postalBranchId: address.postalBranchId
      }
    }

    formStore.form.setValues(dataObj)
  }
}

const setAddressDataHandle = (address, currentId) => {
  selectedAddressID.value = currentId
  if (props.addressType === 'DeliveryAddresses') {
    setDeliveryAddressData(address)
  } else {
    const res = {}
    props.templateSchema.map((field) => {
      if (field.field_name === 'localAddress.name') {
        lodashSet(res, field.field_name, lodashGet(address, 'localAddress'))
      } else {
        lodashSet(res, field.field_name, lodashGet(address, [field.field_name]))
      }
    })
    formStore.form.setValues(res)
  }
  useAddressFormDataAction(address)
}

// Determine if the local address has been modified
const checkIfLocalAddressChanges = () => {
  const localAddressDiffMap = {}
  props.templateSchema.map((field) => {
    if (field.field_name === 'localAddress.name') {
      lodashSet(localAddressDiffMap, field.field_name, 'localAddress')
    } else {
      lodashSet(localAddressDiffMap, field.field_name, field.field_name)
    }
  })

  const foundAddress = addressDataTable.value.find((x) => x.addressId == selectedAddressID.value)
  let isDiff = false

  if (foundAddress) {
    // check each field if it is different
    Object.entries(localAddressDiffMap).forEach((entry) => {
      if (lodashGet(formStore.form.values, entry[0]) != lodashGet(foundAddress, entry[1])) {
        isDiff = true
      }
    })

    if (isDiff) {
      return true
    } else {
      formStore.extraMap.localAddressUserActionEnum =
        client_lookup.addressUserActionEnum.TemporaryUse.toString()
      return false
    }
  }

  return true
}
watch(
  checkIsModifyAddressObj,
  (newValue, oldValue) => {
    checkIsModifyFlag.value = checkIfLocalAddressChanges()
  },
  { deep: true }
)

const { findShowRule, getBindObj } = useVisible()

const isRequired = computed(() => !!props.rules?.find((r) => r.name == 'required' || 'required_ui'))
const isShow = (showRules, deviceType) => {
  if (deviceType === 'mobile') return false
  if (!showRules) return true

  return findShowRule(
    showRules.any || showRules.all,
    showRules.any ? 'any' : 'all',
    getBindObj(route.params.serviceId)
  )
}
const t9nLabel = computed(() => i18n.t(props.label))

const layoutClasses = (layout) => {
  if (layout === 'two-column') {
    return {
      bodyClass: 'flex flex-wrap gap-y-2',
      class: '!w-1/2 px-2',
      direction: 'flex-col',
      labelClass: '!w-full',
      showErrorSpacer: false
    }
  } else {
    return {
      bodyClass: 'flex flex-col gap-y-2',
      showErrorSpacer: false
    }
  }
}
const findComponent = (component) => {
  switch (component) {
    case 'ComboText':
      return ComboText
    case 'DropdownSearch':
      return DropdownSearch
    case 'InfoBar':
      return InfoBar
    case 'TextInput':
      return TextInput
    case 'TwowayDropdown':
      return TwowayDropdown
    case 'DatepickerCalendar':
      return DatepickerCalendar
    case 'TimePicker':
      return TimePicker
    case 'DateTimePicker':
      return DateTimePicker
    case 'RecaptchaTicker':
      return RecaptchaTicker
    case 'InfoLabel':
      return InfoLabel
    case 'RadioGroup':
    case 'radio_group':
      return radio_group
    case 'VisaFileNumber':
      return VisaFileNumber
    case 'CommonButton':
      return CommonButton
    case 'MobileButtonList':
      return MobileButtonList
    case 'Spacer':
      return SpacerComponent
    case 'TextRender':
      return TextRenderer
    case 'CheckBox':
      return CheckboxTicker
    case 'MobileButtonList':
      return MobileButtonList
    default:
      return component
  }
}

onBeforeMount(() => {
  initEnableNewSharedUserAddreses()
  getLocalAddresses()
})
</script>
<script>
export default {
  name: 'AddressFormGroup'
}
</script>

<style lang="scss" scoped></style>
